import React, { useMemo, useState } from 'react';
import { Select, Tooltip } from 'antd';
import { ColumnShape } from 'react-base-table';
import 'react-base-table/styles.css';

import {
  AccountMetricsPanelWrapper,
  AccountMetricsPanelContentWrapper,
  PanelGroupWrapper,
  PanelItemWrapper,
  TitleWrapper,
  StyledSelect,
  PanelItemTitle,
  TableHeaderWrapper,
  PanelItemContent,
  EmptyTableWrapper,
} from './styled';
import {
  METRIC_PANEL_CONFIG,
  METRIC_STAGE_MAP,
  DATE_OPTIONS,
  TABLE_LIST_OPTIONS,
} from '../constant';
import { formatDate, getAllStageCustomersByTimeRange, safeArrayLength } from '../utils';
import { ICustomerOnboardingTimeline } from '../typed';
import { InfoCircleOutlined } from '@ant-design/icons';
import Table from '@components/common/Table';
import { DATE_SHORTCUTS } from '@utils/constants';

const headerRenderer = ({ column }) => {
  const tooltip = column?.columnTitleToolTip;
  return (
    <TableHeaderWrapper>
      <span>{column.title}</span>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <InfoCircleOutlined />
        </Tooltip>
      ) : null}
    </TableHeaderWrapper>
  );
};

const durationColumns = TABLE_LIST_OPTIONS.map((option) => {
  return {
    key: `duration-${option.value}`,
    dataKey: 'stageDurationDays',
    title: option.columnTitle,
    columnTitleToolTip: option.columnTitleToolTip,
    width: 200,
    align: 'right' as const,
    flexGrow: 1,
    cellRenderer: ({ rowData }: { rowData: any }) =>
      rowData.stageDurationDays?.[option.value] ?? '-',
    headerRenderer: headerRenderer,
  };
});

const AccountMetricsPanel: React.FC<{ data?: ICustomerOnboardingTimeline[] }> = ({ data }) => {
  const [dateRange, setDateRange] = useState(DATE_SHORTCUTS.past7Days);
  const [tableListType, setTableListType] = useState(METRIC_STAGE_MAP.awaitingActivation.value);

  const allStageCustomers = getAllStageCustomersByTimeRange(data, dateRange);

  const columns = useMemo(() => {
    return [
      {
        key: 'customer_name',
        dataKey: 'customer_name',
        title: 'Customer Name',
        width: 200,
        align: 'left',
        flexGrow: 1,
        headerRenderer: headerRenderer,
      },
      {
        key: 'start_self_serve_at',
        dataKey: 'start_self_serve_at',
        title: 'Newly Started Self-Serve',
        columnTitleToolTip: 'Enabled Self-Serve in the Portal.',
        width: 200,
        align: 'right' as const,
        flexGrow: 1,
        cellRenderer: ({ cellData }: { cellData: any }) => formatDate(cellData),
        headerRenderer,
      },
      ...durationColumns,
    ] as ColumnShape<any>[];
  }, []);

  const handleTableListTypeChange = (value) => setTableListType(value);

  return (
    <div>
      <TitleWrapper>Metrics</TitleWrapper>

      <AccountMetricsPanelWrapper>
        <Select
          value={dateRange}
          options={DATE_OPTIONS}
          onChange={(value) => setDateRange(value)}
          style={{ width: 320 }}
        />
        <AccountMetricsPanelContentWrapper>
          <PanelGroupWrapper>
            {METRIC_PANEL_CONFIG.map(({ key, stageKey }) => {
              const { label, metricTooltip, value } = METRIC_STAGE_MAP[key];
              return (
                <PanelItemWrapper
                  key={key}
                  hoverable
                  onClick={() => handleTableListTypeChange(value)}
                  active={value === tableListType}
                >
                  <PanelItemTitle>
                    <span>{label}</span>
                    <Tooltip title={metricTooltip}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </PanelItemTitle>
                  <PanelItemContent>
                    {safeArrayLength(allStageCustomers[stageKey])} Accounts
                  </PanelItemContent>
                </PanelItemWrapper>
              );
            })}
          </PanelGroupWrapper>

          <PanelItemWrapper>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              List of
              <StyledSelect
                value={tableListType}
                options={TABLE_LIST_OPTIONS}
                onChange={handleTableListTypeChange}
                popupMatchSelectWidth={false}
              />
            </div>
            <Table
              data={allStageCustomers[tableListType]}
              columns={columns}
              rowKey="customer_id"
              rowHeight={32}
              headerHeight={40}
              emptyRenderer={() => <EmptyTableWrapper>No Data</EmptyTableWrapper>}
              style={{ minHeight: 282, height: '100%' }}
            />
          </PanelItemWrapper>
        </AccountMetricsPanelContentWrapper>
      </AccountMetricsPanelWrapper>
    </div>
  );
};

export default AccountMetricsPanel;
